/**
 * Cover-art palette + determinism (designer handoff — "Vinyl Covers").
 *
 * The sleeve's printed cover is generated from the note's own identity, so
 * the SAME note always prints the SAME jacket and any two notes differ. Two
 * tiny pure functions drive it — a 32-bit FNV-1a hash and a mulberry32 PRNG —
 * both dependency-free and stable cross-platform (they run identically here
 * and in the design source). Nothing in this module touches React or the DOM;
 * the generators in cover-patterns.tsx consume it.
 *
 * The six colourways are complementary hues muted toward the product's warm
 * ground + navy (Factory / Saville → Gondwana lineage) — they read as one
 * house beside the product tokens, never a raw UI swatch. `base` = the jacket
 * field, `ink` = the pattern tone, `glow` = the luminous highlight some
 * patterns use.
 */

import type { PlayableItem } from "@/lib/player/playable-item"

export interface Cover {
  key: string
  name: string
  motif: string
  base: string
  ink: string
  glow: string
  lineage: string
}

export const COVERS: Cover[] = [
  {
    key: "petrol",
    name: "Astral Petrol",
    motif: "Orbits & starfield",
    base: "#2F6B77",
    ink: "#4c96a3",
    glow: "#e6f4f4",
    lineage: "Gondwana celestial",
  },
  {
    key: "jade",
    name: "Saville Jade",
    motif: "Colour wheel",
    base: "#4F8B7A",
    ink: "#2f5d50",
    glow: "#dff0e9",
    lineage: "Factory / Power, Corruption & Lies",
  },
  {
    key: "peri",
    name: "Dusk Periwinkle",
    motif: "Coded colour grid",
    base: "#7C86B6",
    ink: "#4f5a94",
    glow: "#eef0fa",
    lineage: "Saville code",
  },
  {
    key: "rose",
    name: "Fantin Rose",
    motif: "Painterly bloom",
    base: "#C1848D",
    ink: "#9d6470",
    glow: "#f6e6ea",
    lineage: "PC&L Fantin rose",
  },
  {
    key: "coral",
    name: "Crush Coral",
    motif: "Luminous fluid bands",
    base: "#CE8067",
    ink: "#a95f4a",
    glow: "#ffe6cf",
    lineage: "Floating Points — Crush",
  },
  {
    key: "gold",
    name: "Halsall Gold",
    motif: "Rising-sun rays",
    base: "#C6A04D",
    ink: "#9c7a34",
    glow: "#fbf0cf",
    lineage: "Gondwana gold",
  },
]

/** FNV-1a 32-bit over an arbitrary string (verbatim from the design source). */
export function hashFnv1a(str: string): number {
  let x = 2166136261
  for (let i = 0; i < str.length; i++) {
    x ^= str.charCodeAt(i)
    x = Math.imul(x, 16777619)
  }
  return x >>> 0
}

/** mulberry32 — seed → a `() => [0,1)` generator (verbatim from source). */
export function mulberry32(a: number): () => number {
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Per-cover RNG: the hash folds in the cover key so each motif redraws. */
export const rngForSeed = (seed: string, key: string): (() => number) =>
  mulberry32(hashFnv1a(`${seed}|${key}`))

/** Uniform in [a, b) — used throughout the generators. */
export const rr = (rng: () => number, a: number, b: number): number =>
  a + rng() * (b - a)

/**
 * The seed→cover promise. Default = pure hash (`hash(seed) % 6`). The team
 * may later bias topics toward covers (e.g. grief → Fantin Rose) instead of
 * hashing — deferred; the default stays pure so every note is deterministic.
 */
export const selectCover = (seed: string): Cover =>
  COVERS[hashFnv1a(seed) % COVERS.length]

/**
 * The note's identity string. `item.id` is the only stable per-note id on the
 * wire — using it means the jacket survives title/note edits. (The design's
 * example seed was a human "title · author" string; id is the stabler choice.)
 */
export const seedFor = (item: PlayableItem): string => item.id

/** Pattern opacity scalar (design default; not user-exposed for now). */
export const COVER_INTENSITY = 0.65
